import React, { useEffect } from 'react';
import FormModal from '../../components/Modals/FormModal';
import SelectField from '../../components/Forms/Input/SelectField';

import { useCustomer } from '../../hooks/useCustomer';
import { useEntityType } from '../../hooks/useEntityType';
import { useTaxpayerCategory } from '../../hooks/useTaxpayerCategory';
import { useEconomicActivities } from '../../hooks/useEconomicActivities';
import { useLocation } from '../../hooks/useLocation';
import { useDocumentIdentificationType } from '../../hooks/documentIdentificationType/useDocumentIdentificationType';

import CustomerNaturalForm from './forms/CustomerNaturalForm';
import CustomerJuridicoForm from './forms/CustomerJuridicoForm';
import CustomerConsumidorFinalForm from './forms/CustomerConsumidorFinalForm';
import CustomerGeneralForm from './forms/CustomerGeneralForm';
import { useCountry } from '../../hooks/countries/useCountry';
import ToggleSwitch from '../../components/Forms/Input/ToggleSwitch';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
}

const CustomerEditPopup: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const { form, isSubmitting, updateFormField } = useCustomer();

  // Dropdowns
  const { fetchEntityTypeDropdown, entityTypeDropdown } = useEntityType();
  const { fetchTaxpayerCategoryDropdown, taxpayerCategoryDropdown } =
    useTaxpayerCategory();
  const {
    handlerAddEconomicActivity,
    fetchEconomicActivitiesDropdown,
    reloadEconomicActivitiesDropdown,
    economicActivitiesDropdown,
  } = useEconomicActivities();
  const {
    fetchDocumentIdentificationTypeDropdown,
    documentIdentificationTypeDropdown,
  } = useDocumentIdentificationType();
  const {
    fetchDepartments,
    fetchMunicipalitiesByDepartment,
    departments,
    municipalities,
  } = useLocation();

  const { countryDropdown, fetchCountryDropdown } = useCountry();

  // ⚡ Cargar combos cuando se abre
  useEffect(() => {
    if (isOpen) {
      // No hacemos resetForm porque queremos mantener el form cargado desde loadCustomerForEdit
      fetchEntityTypeDropdown();
      fetchTaxpayerCategoryDropdown();
      fetchEconomicActivitiesDropdown();
      fetchDepartments();
      fetchDocumentIdentificationTypeDropdown();
      fetchCountryDropdown('');
    }
  }, [isOpen]);

  // ✅ Si no hay form cargado todavía, puedes mostrar un loader o nada
  if (!form || form.id === null) {
    return null;
  }

  const entityTypeId = Number(form.entityTypeId);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Cliente"
      maxWidth="max-w-6xl"
    >
      <div className="mt-4 space-y-8">
        {/* 🔹 Tipo de Entidad */}
        <div>
          <div className="mb-6 border-b pb-4">
            <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
              Tipo de Entidad
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Este cliente fue registrado con el tipo de entidad que se muestra
              a continuación. Por razones de integridad de datos, no es posible
              modificar este campo.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <SelectField
              iconKey="list"
              label="Tipo de Entidad"
              name="entityTypeId"
              value={form.entityTypeId ?? ''}
              disabled
              onChange={() => {}} // opcional: noop
              options={entityTypeDropdown
                .filter((t) => t.id !== 4)
                .map((t) => ({
                  label: t.name,
                  value: t.id,
                }))}
            />
          </div>
        </div>

        {/* 🔹 Datos dinámicos según tipo */}
        <div>
          <div className="mb-6 border-b pb-4">
            <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
              Datos del Cliente
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Actualiza la información necesaria y guarda los cambios.
            </p>
          </div>

          <div className="mb-6 rounded-xl border border-stroke bg-gray-2/50 p-4 dark:border-strokedark dark:bg-meta-4/30">
            <ToggleSwitch
              label="Cliente predeterminado para ventas y restaurante"
              name="defaultCustomer"
              checked={form.defaultCustomer}
              disabled={isSubmitting}
              onChange={() =>
                updateFormField('defaultCustomer', !form.defaultCustomer)
              }
            />
          </div>

          {entityTypeId === 1 && (
            <CustomerNaturalForm
              form={form}
              updateField={updateFormField}
              taxpayerCategoryDropdown={taxpayerCategoryDropdown}
              economicActivitiesDropdown={economicActivitiesDropdown}
              countryDropdown={countryDropdown}
              departments={departments}
              municipalities={municipalities}
              documentIdentificationTypeDropdown={
                documentIdentificationTypeDropdown
              }
              onFetchMunicipalities={fetchMunicipalitiesByDepartment}
              onCancel={onClose}
              onSubmit={onSubmit}
              onEconomicActivityCreated={handlerAddEconomicActivity}
              onReloadEconomicActivities={reloadEconomicActivitiesDropdown}
              isSubmitting={isSubmitting}
            />
          )}

          {entityTypeId === 2 && (
            <CustomerJuridicoForm
              form={form}
              updateField={updateFormField}
              taxpayerCategoryDropdown={taxpayerCategoryDropdown}
              economicActivitiesDropdown={economicActivitiesDropdown}
              countryDropdown={countryDropdown}
              departments={departments}
              municipalities={municipalities}
              documentIdentificationTypeDropdown={
                documentIdentificationTypeDropdown
              }
              onFetchMunicipalities={fetchMunicipalitiesByDepartment}
              onCancel={onClose}
              onSubmit={onSubmit}
              onEconomicActivityCreated={handlerAddEconomicActivity}
              onReloadEconomicActivities={reloadEconomicActivitiesDropdown}
              isSubmitting={isSubmitting}
            />
          )}

          {entityTypeId === 3 && (
            <CustomerConsumidorFinalForm
              form={form}
              updateField={updateFormField}
              countryDropdown={countryDropdown}
              departments={departments}
              municipalities={municipalities}
              documentIdentificationTypeDropdown={
                documentIdentificationTypeDropdown
              }
              onFetchMunicipalities={fetchMunicipalitiesByDepartment}
              onCancel={onClose}
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
            />
          )}

          {entityTypeId === 5 && (
            <CustomerGeneralForm
              form={form}
              updateField={updateFormField}
              onCancel={onClose}
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>
    </FormModal>
  );
};

export default CustomerEditPopup;
